import React, { useState, useEffect } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import CustomerData from "./stepper/CustomerData";
import TenderData from "./stepper/TenderData";
import FileUpload from "./stepper/FileUpload";
import Transformation from "./stepper/Transformation";
import Mapping from "./stepper/Mapping";
import Enrichments from "./stepper/Enrichments";
import Validation from "./stepper/Validation";
import TenderDocuments from "./TenderDocuments";
import {
  API_URL_TENDER_DETAILS,
  API_URL_TENDER_CUSTOM_COLUMNS,
  steps,
  optionalSteps,
} from "../configuration/Configuration";
import { isPosted } from "../configuration/Utils";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, CircularProgress, Modal, TextField } from "@mui/material";
import { useAuth } from "../context/AuthContext";

export default function NewTender() {
  const [open, setOpen] = useState(false);
  const [targetPanel, setTargetPanel] = useState('input'); // 'info' | 'input'
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editTenderId = searchParams.get("edit_tender_id");
  const isEditMode = Boolean(editTenderId);
  const [isInitialDataLoading, setIsInitialDataLoading] = useState(false);
  const [initialDataError, setInitialDataError] = useState("");

  const [columnTitle, setColumnTitle] = useState();
  const [customColumns, setCustomColumns] = useState([]);
  const [infoCustomColumns, setInfoCustomColumns] = useState([]);

  const [tenderDataState, setTenderDataState] = useState();
  const [mappingDataState, setMappingDataState] = useState();

  const { isAuth, userType, user } = useAuth();
  const { key } = useLocation();

  let stepDone = false;
  const [saveLaneState, setSaveLaneState] = React.useState(false);

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());

  // set upload file columns
  // eslint-disable-next-line
  const [customer, setCustomer] = React.useState("N/A");
  const [customerNotificationEmails, setCustomerNotificationEmails] =
    React.useState([]);
  const customerSettings = (customerData) => {
    if (!customerData) {
      setCustomer("N/A");
      setCustomerNotificationEmails([]);
      return;
    }

    if (typeof customerData === "string") {
      setCustomer(customerData);
      setCustomerNotificationEmails([]);
      return;
    }

    setCustomer(customerData.name || "N/A");
    setCustomerNotificationEmails(customerData.selectedEmails || []);
  };
  // set tender ID
  const [tender, setTender] = React.useState("");
  const tenderID = (id) => {
    setTender(id);
  };
  // set upload file columns and file
  const [uploadFile, setUploadFile] = React.useState({ columns: ["-"] });
  const fileData = (uploadedFile) => {
    setUploadFile(uploadedFile);
  };

  // set mapped Data
  const [mapData, setMappedData] = React.useState({ columns: [""] });
  const mappedData = (data) => {
    setMappedData(data);
  };

  const getColumnLength = (array) => {
    if (array.columns) {
      return array.columns.length;
    } else {
      return array.length;
    }
  };

  const createdTenderId =
    tender?.tenderID?.pk__max ?? (isEditMode ? Number(editTenderId) : null);
  const displayedSteps = isEditMode ? steps.slice(0, 2) : steps;
  const isWizardComplete = isEditMode
    ? activeStep === displayedSteps.length
    : activeStep === 5;
  const canAccessWizard = Boolean(userType?.isAdmin);

  const parseRequestedCapacity = (value) => {
    if (!value) {
      return { capacity: "", transit_time_type: "days" };
    }

    if (typeof value === "object") {
      return {
        capacity: value.capacity || "",
        transit_time_type: value.transit_time_type || "days",
      };
    }

    try {
      const parsedValue = JSON.parse(value);
      if (parsedValue && typeof parsedValue === "object") {
        return {
          capacity: parsedValue.capacity || "",
          transit_time_type: parsedValue.transit_time_type || "days",
        };
      }
    } catch (error) {
      return { capacity: value, transit_time_type: "days" };
    }

    return { capacity: "", transit_time_type: "days" };
  };

  // optional steps are Transformation and Enrichments
  const isStepOptional = (step) => {
    return optionalSteps.includes(step);
  };

  const isStepCompany = (step) => {
    return step === 0 && customer !== "";
  };

  // track skipped steps
  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    stepDone = false;
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    // check if step can be skipped
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    // go to next step and add to list of skipped steps
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
    setSaveLaneState(false);
    stepDone = false;
    setCustomer("");
    setCustomerNotificationEmails([]);
    customerSettings("N/A");
    tenderID("");
    fileData({ columns: ["-"] });
    mappedData({ columns: [""] });
    setMappingDataState(null);
    setTenderDataState(null);
    setCustomColumns([]);
    setInfoCustomColumns([]);
  };

  useEffect(() => {
    let isCancelled = false;

    const loadTenderForEditing = async () => {
      handleReset();
      setInitialDataError("");

      if (!isEditMode) {
        setIsInitialDataLoading(false);
        return;
      }

      if (!isAuth || !editTenderId) {
        setIsInitialDataLoading(true);
        return;
      }

      setIsInitialDataLoading(true);

      try {
        const { data } = await axios.get(API_URL_TENDER_DETAILS(editTenderId));
        const tenderDetails = Array.isArray(data) ? data[0] : data;
        let loadedCustomColumns = [];

        try {
          const { data: customColumnsData } = await axios.get(
            API_URL_TENDER_CUSTOM_COLUMNS(editTenderId)
          );
          loadedCustomColumns = Array.isArray(customColumnsData)
            ? customColumnsData
                .map((column) => ({
                  name: column?.name || "",
                  required: Boolean(column?.required),
                  ...(column?.column_ref ? { columnRef: column.column_ref } : {}),
                }))
                .filter((column) => column.name)
            : [];
        } catch (customColumnsError) {
          console.warn(
            "Could not load custom columns for edit mode:",
            customColumnsError
          );
        }

        if (!tenderDetails) {
          throw new Error("Tender not found.");
        }

        const requestedCapacity = parseRequestedCapacity(
          tenderDetails.requested_capacity
        );

        if (isCancelled) {
          return;
        }

        setCustomColumns(loadedCustomColumns.filter((c) => c.required));
        setInfoCustomColumns(loadedCustomColumns.filter((c) => !c.required));
        customerSettings({
          name: tenderDetails.company || "N/A",
          selectedEmails: tenderDetails.customer_notification_emails || [],
        });
        tenderID({ tenderID: { pk__max: tenderDetails.id } });
        setSaveLaneState(Boolean(tenderDetails.lanes_saved));
        setTenderDataState({
          prio: tenderDetails.priority ?? 2,
          date: tenderDetails.internalDeadline || "",
          date1: tenderDetails.submissionDate || "",
          date2: tenderDetails.contractStart || "",
          date3: tenderDetails.contractEnd || "",
          state: tenderDetails.invoicing || "",
          state2: tenderDetails.paymentTerms ?? "",
          state3: tenderDetails.link || "",
          state4:
            tenderDetails.requested_capacity_text || requestedCapacity.capacity,
          capacityRequired:
            tenderDetails.capacity_required ?? Boolean(requestedCapacity.capacity),
          infoCapacity: Boolean(tenderDetails.info_capacity),
          state5: Boolean(tenderDetails.additional_costs_required),
          state6: Boolean(tenderDetails.margin_required),
          state7: Boolean(tenderDetails.transit_time_required),
          state8: Boolean(tenderDetails.additional_data_required),
          tenderInformation: tenderDetails.tender_information || "",
          responsiblePerson: tenderDetails.responsible_person || "",
          fillingNotes: tenderDetails.filling_notes || "",
          selectedDepartments: tenderDetails.selected_departments || [],
          allLanesDepartmentIds: tenderDetails.all_lanes_departments || [],
          transitTimeType:
            tenderDetails.transit_time_type ||
            requestedCapacity.transit_time_type ||
            "days",
          additionalCostsType:
            tenderDetails.additional_costs_type || "absolute",
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setInitialDataError(
          error.response?.data?.message ||
            error.response?.data?.detail ||
            error.message ||
            "Could not load tender data for editing."
        );
      } finally {
        if (!isCancelled) {
          setIsInitialDataLoading(false);
        }
      }
    };

    loadTenderForEditing();

    return () => {
      isCancelled = true;
    };
  }, [key, editTenderId, isEditMode, isAuth]);

  // define step content
  let component;
  if (activeStep === 0) {
    component = (
      <CustomerData
        func={customerSettings}
        handleNext={handleNext}
        customer={customer}
        selectedCustomerEmails={customerNotificationEmails}
      />
    );
    if (customer !== "N/A") {
      stepDone = true;
    }
  } else if (activeStep === 1) {
    component = (
      <TenderData
        customer={customer}
        func={tenderID}
        handleNext={handleNext}
        setOpenInfo={() => { setTargetPanel('info'); setOpen(true); }}
        setOpenInput={() => { setTargetPanel('input'); setOpen(true); }}
        customColumns={customColumns}
        setCustomColumns={setCustomColumns}
        infoCustomColumns={infoCustomColumns}
        setInfoCustomColumns={setInfoCustomColumns}
        tenderDataState={tenderDataState}
        setTenderDataState={setTenderDataState}
        customerNotificationEmails={customerNotificationEmails}
        isEditMode={isEditMode}
        editTenderId={editTenderId}
        createdTenderId={!isEditMode ? createdTenderId : null}
        username={user}
        canEditDepartments={!isEditMode || Boolean(userType?.isAdmin)}
      />
    );

    if (tender !== "") {
      stepDone = true;
    }
  } else if (activeStep === 2) {
    component = <FileUpload func={fileData} handleNext={handleNext} />;
    if (getColumnLength(uploadFile) > 1) {
      stepDone = true;
    }
  } else if (activeStep === 3) {
    /*
  else if (activeStep === 3) {
    component = <Transformation />;
    stepDone = true;
  } 
  */
    component = (
      <Mapping
        customColumns={[...infoCustomColumns, ...customColumns]}
        uploadFile={uploadFile}
        tender={tender}
        func={mappedData}
        handleNext={handleNext}
        mappingDataState={mappingDataState}
        setMappingDataState={setMappingDataState}
      />
    );
    if (getColumnLength(mapData) > 1) {
      stepDone = true;
    }
  } else if (activeStep === 4) {
    /*
  else if (activeStep === 5) {
    component = <Enrichments />;
    stepDone = true;
  } 
  */
    component = (
      <Validation
        mapData={mapData}
        tender={tender}
        func={setSaveLaneState}
        handleNext={handleNext}
      />
    );
    if (isPosted(saveLaneState)) {
      stepDone = true;
    }
  } else {
    component = <CustomerData />;
  }

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  // return stepper for definition of new tender
  return (
    <>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {targetPanel === 'info' ? 'Add information column' : 'Add input column'}
          </Typography>
          <Grid item xs={12} sx={{ mt: 3 }} align="left"></Grid>
          <TextField
            id="filled-basic"
            onChange={(event) => setColumnTitle(event.target.value)}
            value={columnTitle}
            type="text"
            label="Column Name"
          />
          <Grid item xs={12} sx={{ mt: 3 }} align="left"></Grid>

          <Grid item xs={12} align="center">
            <Button
              variant="contained"
              size="small"
              color="secondary"
              onClick={() => {
                if (columnTitle) {
                  if (targetPanel === 'info') {
                    setInfoCustomColumns([...infoCustomColumns, { name: columnTitle, required: false }]);
                  } else {
                    setCustomColumns([...customColumns, { name: columnTitle, required: true }]);
                  }
                }
                setColumnTitle("");
                setOpen(false);
              }}
            >
              Save
            </Button>
          </Grid>
        </Box>
      </Modal>

      <Grid container spacing={2}>
        {userType && !userType.isAdmin && isAuth && !isEditMode && (
          <Grid item xs={12}>
            <Typography>Tender Management only!</Typography>
          </Grid>
        )}
        {isEditMode && userType && !canAccessWizard && !isInitialDataLoading && (
          <Grid item xs={12}>
            <Alert severity="warning">
              You do not have permission to edit tenders.
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}></Grid>

        {canAccessWizard && (
          <Grid container spacing={2} justify="flex-end" alignItems="center">
            {/* NEW */}
            <Grid item xs={0.5}></Grid>
            <Grid item xs={8}>
              <Typography variant="h5" align="left">
                {isEditMode
                  ? "Edit your tender with the creation wizard."
                  : "Create your tender in seven steps."}
              </Typography>
            </Grid>
            <Grid item xs={12}></Grid>
            <Divider style={{ width: "100%" }} flexitem />
            <Grid item xs={12}></Grid>
          </Grid>
        )}
        <Grid item xs={0.5}></Grid>
        {canAccessWizard && (
          <Grid item xs={11}>
            <Box sx={{ width: "100%" }}>
              {isInitialDataLoading && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 6,
                  }}
                >
                  <CircularProgress size={24} />
                  <Typography>Loading tender data...</Typography>
                </Box>
              )}
              {!isInitialDataLoading && initialDataError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {initialDataError}
                </Alert>
              )}
              {!isInitialDataLoading && !initialDataError && (
                <>
              <Stepper activeStep={activeStep}>
                {displayedSteps.map((label, index) => {
                  const stepProps = {};
                  const labelProps = {};
                  if (isStepOptional(index)) {
                    labelProps.optional = (
                      <Typography variant="caption">Optional</Typography>
                    );
                  }
                  if (isStepCompany(index)) {
                    labelProps.optional = (
                      <Typography variant="caption">
                        Company: {customer}
                      </Typography>
                    );
                  }
                  if (isStepSkipped(index)) {
                    stepProps.completed = false;
                  }
                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              {isWizardComplete ? (
                <React.Fragment>
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    {isEditMode
                      ? "Tender updated successfully."
                      : "All Steps Completed - You are Done!"}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    <Box sx={{ flex: "1 1 auto" }} />
                    {isEditMode ? (
                      <Button
                        onClick={() =>
                          navigate(`/AdvanceTender?tender_id=${editTenderId}`)
                        }
                        variant="outlined"
                        size="large"
                      >
                        Back to Manage Tender
                      </Button>
                    ) : (
                      <Button
                        onClick={handleReset}
                        variant="outlined"
                        size="large"
                      >
                        Enter Next Tender
                      </Button>
                    )}
                  </Box>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ m: 1 }} />
                    {component}
                    {createdTenderId && activeStep >= 2 && (
                      <Box sx={{ mt: 4, mb: 2 }} key={`documents-${createdTenderId}`}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle1" align="left" sx={{ mb: 1 }}>
                          Tender Documents
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="left"
                          sx={{ mb: 2 }}
                        >
                          Upload supporting tender documents while finishing the creation flow.
                        </Typography>
                        <TenderDocuments
                          key={createdTenderId}
                          tenderId={createdTenderId}
                          username={user}
                          userType={userType}
                        />
                      </Box>
                    )}
                    <Box sx={{ m: 1 }} />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    <Button
                      color="inherit"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                    <Box sx={{ flex: "1 1 auto" }} />
                    {isStepOptional(activeStep) && (
                      <Button
                        color="inherit"
                        onClick={handleSkip}
                        sx={{ mr: 1 }}
                      >
                        Skip
                      </Button>
                    )}
                    {/* In edit mode each step (CustomerData, TenderData) has its own
                        action button that commits pending input and saves via the API
                        before advancing. A separate generic Next/Finish button here would
                        just advance the step without saving, silently dropping changes. */}
                    {!isEditMode && stepDone && (
                      <Button onClick={handleNext}>
                        {activeStep === displayedSteps.length - 1 ? "Finish" : "Next"}
                      </Button>
                    )}
                    {!isEditMode && !stepDone && (
                      <Button disabled onClick={handleNext}>
                        {activeStep === displayedSteps.length - 1 ? "Finish" : "Next"}
                      </Button>
                    )}
                  </Box>
                </React.Fragment>
              )}
                </>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  );
}
